import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useGetMyOrdersQuery } from "@/features/api/paymentApi";

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const {
    data,
    isLoading,
    isError,
  } = useGetMyOrdersQuery(undefined, {
    skip: !user,
  });

  /* ---------------- SAFE SORT (RTK Query returns frozen data) ---------------- */
  const orders = useMemo(() => {
    if (!Array.isArray(data?.data)) return [];

    return [...data.data].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [data]);

  /* ---------------- LOADING ---------------- */
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  /* ---------------- ERROR ---------------- */
  if (isError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-semibold mb-2 text-red-600">
          Failed to load orders
        </h2>
        <Button onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  /* ---------------- EMPTY ---------------- */
  if (!orders.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-semibold mb-2">
          No purchases yet 🛒
        </h2>
        <p className="text-muted-foreground mb-6">
          Browse courses and start learning today.
        </p>
        <Button onClick={() => navigate("/courses")}>
          Browse Courses
        </Button>
      </div>
    );
  }

  /* ---------------- STATUS BADGE STYLE ---------------- */
  const getStatusStyle = (status) => {
    switch (status) {
      case "SUCCESS":
        return "bg-green-600 text-white";
      case "PENDING":
        return "bg-yellow-500 text-white";
      case "FAILED":
        return "bg-red-600 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 min-h-screen">
      <h1 className="text-4xl font-bold text-center mb-8">
        My Orders
      </h1>

      <Table>
        <TableCaption>Your purchase history</TableCaption>

        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Transaction ID</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {orders.map((order, index) => {
            const isCourse = order.productType === "COURSE";

            return (
              <TableRow key={order._id}>
                <TableCell>{index + 1}</TableCell>

                <TableCell className="font-medium">
                  {order.courseName ||
                    order.courseId?.title ||
                    "Product"}
                </TableCell>

                <TableCell>
                  {order.createdAt
                    ? new Date(order.createdAt).toLocaleDateString()
                    : "—"}
                </TableCell>

                <TableCell>
                  ₹{Number(order.amount || 0).toFixed(2)}
                </TableCell>

                <TableCell>
                  <Badge className={getStatusStyle(order.status)}>
                    {order.status}
                  </Badge>
                </TableCell>

                <TableCell className="text-xs break-all">
                  {order.transactionId || "—"}
                </TableCell>

                <TableCell className="text-right">
                  {order.status === "SUCCESS" &&
                    isCourse &&
                    order.courseId?.slug && (
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/courses/${order.courseId.slug}/learn`
                          )
                        }
                      >
                        View Course
                      </Button>
                    )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default MyOrdersPage;